import type { RefinanceState } from '@/store/slices/refinanceSlice';
import { amortizeCurrentLoan } from '@/utils/currentLoanAmortization';

export type KeepVsRefiComparison = {
  currentMonthlyTotal: number;
  newMonthlyTotal: number;
  monthlyDelta: number;
  closingCosts: number;
  breakevenMonths: number | null;
  currentRemainingInterest: number;
  newLifetimeInterest: number;
  interestSavingsIfRefi: number;
  currentRemainingMonths: number;
  effectivePayoff: number;
  newLoanAmount: number;
  /** Cash needed at close to pay off existing loan (payoff − new loan + closing), or negative = cash out. */
  netCashAtClose: number;
  cashOutAmount: number;
  usedDerivedPayment: boolean;
};

/**
 * Side-by-side keep-current vs refinance estimate using remaining amortization
 * of the existing loan and optional statement payoff.
 */
export function computeKeepVsRefiComparison(opts: {
  refinance: RefinanceState;
  newLoanAmount: number;
  newMonthlyTotal: number;
  newLifetimeInterest: number;
  closingCosts: number;
}): KeepVsRefiComparison {
  const {
    refinance,
    newLoanAmount,
    newMonthlyTotal,
    newLifetimeInterest,
    closingCosts,
  } = opts;

  const currentMonthlyTotal =
    refinance.currentLoan.monthlyPayment +
    refinance.currentLoan.escrowTaxes +
    refinance.currentLoan.escrowInsurance +
    refinance.currentLoan.escrowHoa;

  const amort = amortizeCurrentLoan({
    balance: refinance.currentLoan.balance,
    annualRatePct: refinance.currentLoan.rate,
    remainingTermYears: refinance.currentLoan.termYears,
    monthlyPayment: refinance.currentLoan.monthlyPayment,
    estimatedPayoff: refinance.statement.estimatedPayoff,
  });

  const monthlyDelta = currentMonthlyTotal - newMonthlyTotal;
  const breakevenMonths =
    monthlyDelta > 0 && closingCosts > 0
      ? Math.round(closingCosts / monthlyDelta)
      : null;

  const cashOutAmount =
    refinance.selection.refiType === 'cash_out'
      ? refinance.newLoan.cashOutAmount || 0
      : Math.max(0, newLoanAmount - amort.effectivePayoff);

  const prepay = Number(refinance.statement.prepaymentPenalty) || 0;

  // Positive = borrower brings cash; negative = borrower receives cash (cash-out / over-advance).
  const netCashAtClose =
    amort.effectivePayoff + closingCosts + prepay - newLoanAmount;

  return {
    currentMonthlyTotal,
    newMonthlyTotal,
    monthlyDelta,
    closingCosts,
    breakevenMonths,
    currentRemainingInterest: amort.remainingInterest,
    newLifetimeInterest,
    interestSavingsIfRefi: amort.remainingInterest - newLifetimeInterest,
    currentRemainingMonths: amort.remainingMonths,
    effectivePayoff: amort.effectivePayoff,
    newLoanAmount,
    netCashAtClose,
    cashOutAmount,
    usedDerivedPayment: amort.usedDerivedPayment,
  };
}
